import { useCallback, useContext, useEffect, useState } from 'react';
import { ExpenseEntryData, ExpenseEntryDocument } from '../baseTypes';
import { OrderByClause, WhereClause } from '../queryBuilder';
import { useAuthenticatedSession } from '@/authenticatedSession/AuthenticatedSessionContext';
import { makeLoadState, useLoadState } from '@/hooks/useLoadState';

export function useExpenseEntries(props: {
  where?: WhereClause<ExpenseEntryData>[];
  orderBy?: OrderByClause<ExpenseEntryData>;
}) {
  const { where, orderBy } = props;
  const authSession = useAuthenticatedSession();
  const { loadState, setLoadState } = useLoadState<ExpenseEntryDocument[]>({
    status: 'loading',
  });

  useEffect(() => {
    setLoadState({ status: 'loading' });
    if (!authSession) {
      return;
    }
    return authSession.db.subscribeToExpenseEntriesChanges(
      where ?? [],
      orderBy ? [orderBy] : [],
      (collectionSnapshot) => {
        setLoadState((previousLoadState) => {
          if (previousLoadState.status !== 'loaded') {
            return makeLoadState({
              status: 'loaded',
              data: collectionSnapshot.docs.map((docSnapshot) => ({
                ...docSnapshot.data(),
                id: docSnapshot.id,
              })),
            });
          }
          const previousById = new Map(
            previousLoadState.data.map((obj) => [obj.id, obj]),
          );
          const changedIds = new Set(
            collectionSnapshot
              .docChanges()
              .filter((change) => change.type !== 'removed')
              .map((change) => change.doc.id),
          );
          const newData = collectionSnapshot.docs.map((doc) => {
            const previousObj = previousById.get(doc.id);
            if (previousObj && !changedIds.has(doc.id)) {
              return previousObj;
            }
            return {
              ...doc.data(),
              id: doc.id,
            };
          });
          return makeLoadState({
            status: 'loaded',
            data: newData,
          });
        });
      },
    );
  }, [JSON.stringify(where), JSON.stringify(orderBy), authSession]);

  return loadState;
}

export function useExpenseEntry(
  id: string | null,
): ExpenseEntryDocument | undefined {
  const authSession = useAuthenticatedSession();
  const [doc, setDoc] = useState<ExpenseEntryDocument | undefined>(undefined);

  useEffect(() => {
    if (!authSession || !id) {
      setDoc(undefined);
      return;
    }
    return authSession.db.subscribeToExpenseEntryChanges(id, setDoc);
  }, [authSession, id]);

  return doc;
}

export function useExpenseEntryAdd() {
  const authSession = useAuthenticatedSession();
  return useCallback(async (data: ExpenseEntryData) => {
    if (!authSession) {
      return;
    }
    return await authSession.db.addExpenseEntry(data);
  }, []);
}
